/**
 * Unified Tab System - Route-Derived Tabs Hook
 * @module lib/tabs/useRouteTabs
 *
 * Summary fast-path: check summary for O(1) module/action lookups
 * before falling back to can() for scope-aware checks.
 */

import { parsePermission } from "@lib/permissions/parser";
import type { PermissionsSummary } from "@lib/permissions/summary.types";
import { usePermissionStore } from "@lib/stores/permissions.store";
import { useLocation, useRouter } from "@tanstack/react-router";
import { useMemo } from "react";
import { sortTabs } from "./config";
import {
  filterTabRoutes,
  findAllRoutes,
  getRouteStaticData,
  normalizePath,
  stripAppPrefix,
} from "./route-utils";
import type { DisabledReason, Tab, TabStaticData } from "./types";

/**
 * Options for useRouteTabs
 */
export interface UseRouteTabsOptions {
  /** Module base path (e.g., '/rh' or '/rh/conges') */
  modulePath: string;
}

interface PermissionMethods {
  can: (required: string) => boolean;
  canSelf: (required: string) => boolean;
  canManage: (required: string) => boolean;
  canInDepartment: (required: string, targetDeptPath?: string) => boolean;
  canInFiliale: (required: string, targetFilialeCode?: string) => boolean;
  hasWildcard: boolean;
  context: {
    is_manager?: boolean;
    personne_id?: string;
    dept_path?: string;
  } | null;
}

/**
 * Check tab permission and return visibility/actionability
 * Summary O(1) first, then fallback to store methods for scope-aware checks
 */
function checkTabPermission(
  staticData: TabStaticData | undefined,
  permissions: PermissionMethods,
  summary: PermissionsSummary | null,
): {
  visible: boolean;
  actionable: boolean;
  reason: DisabledReason | null;
} {
  const requiredPermission = staticData?.permission;

  if (!requiredPermission) {
    return { visible: true, actionable: true, reason: null };
  }

  if (permissions.hasWildcard) {
    return { visible: true, actionable: true, reason: null };
  }

  // Summary O(1) check first
  let visible = false;
  if (summary) {
    const parsed = parsePermission(requiredPermission);
    if (parsed) {
      const moduleResources = summary.module_actions[parsed.module];
      if (moduleResources) {
        const resourceActions = moduleResources[parsed.resource] || moduleResources["*"];
        if (resourceActions) {
          visible = resourceActions.includes(parsed.action) || resourceActions.includes("*");
        }
      }
    }
  }

  // Fallback to can() if summary check failed or summary is null
  if (!visible) {
    visible = permissions.can(requiredPermission);
  }

  if (!visible) {
    return { visible: false, actionable: false, reason: "permission_denied" };
  }

  // Actionable logic unchanged — scope-aware, uses store methods
  const actionable =
    permissions.canSelf(requiredPermission) ||
    permissions.canManage(requiredPermission) ||
    permissions.canInDepartment(requiredPermission) ||
    permissions.canInFiliale(requiredPermission) ||
    permissions.can(requiredPermission);

  if (!actionable && permissions.context) {
    const { is_manager, personne_id, dept_path } = permissions.context;

    if (is_manager !== true && requiredPermission.includes(":scope:managed")) {
      return { visible: true, actionable: false, reason: "not_manager" };
    }
    if (!personne_id && requiredPermission.includes(":scope:self")) {
      return { visible: true, actionable: false, reason: "not_self_data" };
    }
    if (!dept_path && requiredPermission.includes(":scope:dept")) {
      return {
        visible: true,
        actionable: false,
        reason: "no_department",
      };
    }
  }

  return { visible: true, actionable: actionable, reason: null };
}

/**
 * Convert a route record to a Tab object
 */
function routeToTab(
  id: string,
  staticData: TabStaticData | undefined,
  permissions: PermissionMethods,
  summary: PermissionsSummary | null,
): Tab | null {
  const { visible, actionable, reason } = checkTabPermission(staticData, permissions, summary);

  if (!visible) return null;

  const fullPath = normalizePath(stripAppPrefix(id.endsWith("/") ? id.slice(0, -1) : id));
  // Support both tab_icon (preferred) and icon (legacy) from route staticData
  const IconComponent = staticData?.tab_icon || staticData?.icon || undefined;

  return {
    id,
    title: staticData?.title || "Untitled",
    path: fullPath,
    icon: IconComponent,
    order: staticData?.order ?? 999,
    badge: staticData?.badge ?? undefined,
    isActive: false, // Set by active tab detection
    isDisabled: !actionable,
    disabledReason: reason ?? undefined,
  };
}

/**
 * Hook to derive tabs from the route tree
 * Replaces useModuleTabs with TypeScript + store-based permission checking
 */
export function useRouteTabs({ modulePath }: UseRouteTabsOptions) {
  const router = useRouter();
  const location = useLocation();

  // Get permission methods from the store (scope helpers are on the store itself)
  const can = usePermissionStore((s) => s.can);
  const canSelf = usePermissionStore((s) => s.canSelf);
  const canManage = usePermissionStore((s) => s.canManage);
  const canInDepartment = usePermissionStore((s) => s.canInDepartment);
  const canInFiliale = usePermissionStore((s) => s.canInFiliale);
  const hasWildcard = usePermissionStore((s) => s.hasWildcard);
  const context = usePermissionStore((s) => s.context);
  const summary = usePermissionStore((s) => s.summary);

  const permissions: PermissionMethods = useMemo(
    () => ({
      can,
      canSelf,
      canManage,
      canInDepartment,
      canInFiliale,
      hasWildcard,
      context,
    }),
    [can, canSelf, canManage, canInDepartment, canInFiliale, hasWildcard, context],
  );

  const currentPathname = location.pathname;

  // Find all routes in the tree
  const allRoutes = useMemo(() => {
    return findAllRoutes(router.routeTree || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.routeTree]);

  // Build tabs
  const tabs = useMemo((): Tab[] => {
    const normalizedModulePath = normalizePath(stripAppPrefix(modulePath));

    const result: Tab[] = [];

    // Find module root for first tab
    const rootMatch = allRoutes.find((r) => {
      const normalizedRouteId = normalizePath(stripAppPrefix(r.id));
      return normalizedRouteId === normalizedModulePath;
    });

    if (rootMatch) {
      const rootStatic = getRouteStaticData(rootMatch as unknown as Record<string, unknown>);
      const { visible } = checkTabPermission(rootStatic, permissions, summary);

      if (visible) {
        const IconComponent = rootStatic?.tab_icon || rootStatic?.icon || undefined;
        result.push({
          id: modulePath,
          title: rootStatic?.title || "Dashboard",
          path: normalizedModulePath,
          icon: IconComponent,
          order: 0, // Dashboard always first
          badge: rootStatic?.badge ?? undefined,
          isActive: false,
          isDisabled: false,
          useExactMatch: true,
        });
      }
    }

    // Filter and add child tabs
    const childRoutes = filterTabRoutes(allRoutes, modulePath);

    for (const route of childRoutes) {
      const staticData = getRouteStaticData(route as unknown as Record<string, unknown>);

      // Skip routes that don't want to show in tab
      if (staticData?.showInTab !== true) continue;

      const tab = routeToTab(route.id, staticData, permissions, summary);
      if (tab) result.push(tab);
    }

    return sortTabs(result);
  }, [allRoutes, modulePath, permissions, summary]);

  // Determine active tab
  const activeTab = useMemo((): string | null => {
    if (!tabs.length || !currentPathname) return null;

    const normalizedCurrent = normalizePath(stripAppPrefix(currentPathname));

    // Exact match first
    const exactMatch = tabs.find((tab) => normalizePath(tab.path) === normalizedCurrent);
    if (exactMatch) return exactMatch.path;

    // Then prefix match (child route)
    const parentMatch = tabs.find((tab) =>
      normalizedCurrent.startsWith(`${normalizePath(tab.path)}/`),
    );

    return parentMatch?.path ?? null;
  }, [tabs, currentPathname]);

  // Set isActive on the matching tab
  const tabsWithActive = useMemo((): Tab[] => {
    return tabs.map((tab) => ({
      ...tab,
      isActive: tab.path === activeTab,
    }));
  }, [tabs, activeTab]);

  return {
    tabs: tabsWithActive,
    activeTab,
    isLoading: false,
    error: null,
  };
}
